import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const PrintModeSetting = ({ themeId, themePageMaster }) => {
    // 상태 관리
    const [printWaitingPage, setPrintWaitingPage] = useState({
        themePageMaster: themePageMaster,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // API 호출로 데이터 가져오기
    useEffect(() => {
        const fetchPrintWaitingPage = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const data = await designApi.getPrintWaitingPage(themeId);

                setPrintWaitingPage({
                    ...data
                });

                console.log("API 호출 성공:", data);
            } catch (err) {
                console.error("API 호출 실패:", err);
                setError(err);
                // 에러 발생 시 기본 데이터 유지
            } finally {
                setIsLoading(false);
            }
        };

        fetchPrintWaitingPage();
    }, [themeId]);

    // 입력값 변경 핸들러
    const handleInputChange = (field, value) => {
        setPrintWaitingPage(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 리소스 업로드 처리 함수
    const handleResourceUploaded = (resourceType, resourceId) => {
        setPrintWaitingPage({
            ...printWaitingPage,
            [resourceType]: resourceId
        });
    };

    // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
    useEffect(() => {
        const eventName = `savePageDesign_print_mode`; // 각 컴포넌트마다 고유한 이벤트 이름

        const saveHandler = async (event) => {
            try {
                // 저장 중임을 표시
                setIsLoading(true);

                // 자체 로직을 사용하여 데이터 저장
                const dataToSave = {
                    ...printWaitingPage,
                    themeId: event.detail.themeId // themeId를 명시적으로 포함
                };
                const isSucceed = await designApi.savePrintWaitingPage(event.detail.themeId, dataToSave);

                if(isSucceed) {
                    // 성공 메시지
                    alert("저장되었습니다.");
                } else {
                    // 실패 메시지
                    alert("저장에 실패했습니다.");
                }

            } catch (error) {
                // 오류 처리
                console.error("저장 실패:", error);
                alert("저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        // 이벤트 리스너 등록
        document.addEventListener(eventName, saveHandler);

        // 컴포넌트 언마운트 시 리스너 제거
        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [printWaitingPage, themeId]); // 의존성 배열에 필요한 상태 포함

    return (
        <div className="design-settings">
            {/* 언어 선택 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">언어 선택</h4>
                <div className="language-select-container">
                    <select
                        className="language-select"
                        value={printWaitingPage.languageCode || "ko"}
                        onChange={(e) =>
                            handleInputChange('languageCode', e.target.value)
                        }
                    >
                        <option value="ko">한국어</option>
                        <option value="en">영어</option>
                        <option value="jp">일본어</option>
                        <option value="cn">중국어</option>
                    </select>
                    <i className="ri-arrow-down-s-line select-arrow"></i>
                </div>
            </div>

            {/* 배경 이미지 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">배경 이미지</h4>
                <ResourceUploadField
                    label=""
                    resourceId={printWaitingPage.backgroundResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("backgroundResourceId", id)
                    }
                    acceptTypes="image/*"
                />
            </div>

            {/* 배경 음악 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">배경 음악</h4>
                <ResourceUploadField
                    label=""
                    resourceId={printWaitingPage.backgroundMusicResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("backgroundMusicResourceId", id)
                    }
                    acceptTypes="audio/*"
                />
            </div>

            {/* 광고영상 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">광고영상</h4>
                <ResourceUploadField
                    label=""
                    resourceId={printWaitingPage.advertisementVideoResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("advertisementVideoResourceId", id)
                    }
                    acceptTypes="video/*"
                />
            </div>

            {/* 상단 문구 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">상단 문구</h4>
                <div className="settings-row">
                    <div className="settings-input">
                        <input
                            type="text"
                            placeholder="사진을 출력하고 있어요"
                            className="text-input"
                            value={printWaitingPage.titleText || ''}
                            onChange={(e) => handleInputChange('titleText', e.target.value)}
                        />
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">색상</div>
                    <div className="color-picker-container">
                        <input
                            type="text"
                            className="color-input"
                            value={printWaitingPage.titleTextColor || '#FFFFFF'}
                            readOnly
                        />
                        <input
                            type="color"
                            className="color-picker"
                            value={printWaitingPage.titleTextColor || '#FFFFFF'}
                            onChange={(e) => handleInputChange('titleTextColor', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* 안내 문구 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">안내 문구</h4>
                <div className="settings-row">
                    <div className="settings-input">
                        <input
                            type="text"
                            placeholder="사진 출력을 기다리는 동안 아래 QR 코드를 스캔해 사진을 다운받을 수 있어요"
                            className="text-input"
                            value={printWaitingPage.guideText || ''}
                            onChange={(e) => handleInputChange('guideText', e.target.value)}
                        />
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">색상</div>
                    <div className="color-picker-container">
                        <input
                            type="text"
                            className="color-input"
                            value={printWaitingPage.guideTextColor || '#FFFFFF'}
                            readOnly
                        />
                        <input
                            type="color"
                            className="color-picker"
                            value={printWaitingPage.guideTextColor || '#FFFFFF'}
                            onChange={(e) => handleInputChange('guideTextColor', e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintModeSetting;